import React, { useRef, useEffect, useCallback } from 'react';
import * as THREE from 'three';

// ---------------------------------------------------------------------------
// AxisGizmo — a Blender/Maya-style orientation cube overlay.
// Renders on a small canvas; clicking an axis dot snaps the main camera to
// that axis, dragging rotates the main camera.
// ---------------------------------------------------------------------------

export interface AxisGizmoProps {
  /** Current camera world position — drives the gizmo orientation. */
  camPos: { x: number; y: number; z: number };
  /** Canvas size in CSS/logical pixels (gizmo is always square). */
  size?: number;
  /** Called when user clicks an axis dot: target is the new camera position. */
  onSnap: (target: THREE.Vector3) => void;
  /** Called on every mousemove while dragging; dx/dy are pixel deltas. */
  onOrbitDelta: (dx: number, dy: number) => void;
}

// Positive axes first; each has a corresponding negative entry immediately after.
const AXIS_DEFS = [
  { dir: new THREE.Vector3( 1,  0,  0), color: '#e55',  label: 'X', neg: false },
  { dir: new THREE.Vector3(-1,  0,  0), color: '#7a2222', label: '',  neg: true  },
  { dir: new THREE.Vector3( 0,  1,  0), color: '#4c4',  label: 'Y', neg: false },
  { dir: new THREE.Vector3( 0, -1,  0), color: '#1a4a1a', label: '',  neg: true  },
  { dir: new THREE.Vector3( 0,  0,  1), color: '#57f',  label: 'Z', neg: false },
  { dir: new THREE.Vector3( 0,  0, -1), color: '#1a2a6a', label: '',  neg: true  },
] as const;

interface Projected {
  index: number;
  dir: THREE.Vector3;
  label: string;
  color: string;
  neg: boolean;
  x: number;
  y: number;
  depth: number;
}

const AxisGizmo: React.FC<AxisGizmoProps> = ({
  camPos,
  size = 96,
  onSnap,
  onOrbitDelta,
}) => {
  const canvasRef   = useRef<HTMLCanvasElement>(null);
  const draggingRef = useRef(false);
  const lastMouseRef = useRef({ x: 0, y: 0 });
  const hoveredRef  = useRef<number | null>(null);

  const half       = size / 2;
  const armRadius  = half - 16; // distance from center to projected axis endpoint

  // ------------------------------------------------------------------
  // Build projection for the current camera orientation.
  // Uses a throwaway OrthographicCamera pointing from camPos direction.
  // ------------------------------------------------------------------
  const getProjected = useCallback((): Projected[] => {
    const camVec  = new THREE.Vector3(camPos.x, camPos.y, camPos.z);
    const camNorm = camVec.length() > 0 ? camVec.clone().normalize() : new THREE.Vector3(0, 0, 1);

    const gizmoCam = new THREE.OrthographicCamera(-1.4, 1.4, 1.4, -1.4, 0.1, 100);
    gizmoCam.position.copy(camNorm.clone().multiplyScalar(4));
    // Prevent gimbal lock when camera is near a vertical pole
    gizmoCam.up.set(Math.abs(camNorm.y) > 0.9 ? 1 : 0, Math.abs(camNorm.y) > 0.9 ? 0 : 1, 0);
    gizmoCam.lookAt(0, 0, 0);
    gizmoCam.updateMatrixWorld();

    const vpMatrix = new THREE.Matrix4().multiplyMatrices(
      gizmoCam.projectionMatrix,
      gizmoCam.matrixWorldInverse,
    );

    return AXIS_DEFS.map((ax, i) => {
      const pt = ax.dir.clone().applyMatrix4(vpMatrix);
      return {
        index: i,
        dir:   ax.dir as unknown as THREE.Vector3,
        label: ax.label,
        color: ax.color,
        neg:   ax.neg,
        x:     half + pt.x * armRadius,
        y:     half - pt.y * armRadius,
        depth: pt.z,
      };
    });
  }, [camPos, half, armRadius]);

  // ------------------------------------------------------------------
  // Draw the gizmo onto the canvas.
  // ------------------------------------------------------------------
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, size, size);

    const projected = getProjected();
    // Paint farthest items first
    const order = [...projected].sort((a, b) => a.depth - b.depth);

    // ── Axis lines ──────────────────────────────────────────────────
    for (const ax of order) {
      ctx.globalAlpha = ax.neg ? 0.4 : 1.0;
      ctx.strokeStyle  = ax.color;
      ctx.lineWidth    = ax.neg ? 1.5 : 2.5;
      ctx.lineCap      = 'round';
      ctx.beginPath();
      ctx.moveTo(half, half);
      ctx.lineTo(ax.x, ax.y);
      ctx.stroke();
    }

    // ── Centre nub ──────────────────────────────────────────────────
    ctx.globalAlpha = 1;
    ctx.fillStyle   = 'rgba(255,255,255,0.25)';
    ctx.beginPath();
    ctx.arc(half, half, 4, 0, Math.PI * 2);
    ctx.fill();

    // ── Endpoint dots + labels ──────────────────────────────────────
    for (const ax of order) {
      const r       = ax.neg ? 5 : 9;
      const hovered = hoveredRef.current === ax.index;
      ctx.globalAlpha = ax.neg ? 0.5 : 1.0;

      // Dot shadow for depth
      if (!ax.neg) {
        ctx.shadowColor = 'rgba(0,0,0,0.55)';
        ctx.shadowBlur  = 4;
      }
      ctx.fillStyle = ax.color;
      ctx.beginPath();
      ctx.arc(ax.x, ax.y, r + (hovered ? 2 : 0), 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;

      // Axis letter
      if (!ax.neg && ax.label) {
        ctx.globalAlpha    = 1.0;
        ctx.fillStyle      = '#fff';
        ctx.font           = 'bold 8px sans-serif';
        ctx.textAlign      = 'center';
        ctx.textBaseline   = 'middle';
        ctx.fillText(ax.label, ax.x, ax.y);
      }
    }

    ctx.globalAlpha = 1.0;
  }, [getProjected, half, size]);

  // Redraw whenever camera position changes
  useEffect(() => { draw(); }, [draw]);

  // ------------------------------------------------------------------
  // Hit-test: which axis endpoint (if any) is at canvas-local (px, py)?
  // ------------------------------------------------------------------
  const hitTest = useCallback((px: number, py: number): number | null => {
    const projected = getProjected();
    let best     = null as number | null;
    let bestDist = Infinity;
    for (const ax of projected) {
      const d   = Math.hypot(px - ax.x, py - ax.y);
      const thr = ax.neg ? 9 : 14;
      if (d < thr && d < bestDist) { bestDist = d; best = ax.index; }
    }
    return best;
  }, [getProjected]);

  // ------------------------------------------------------------------
  // Mouse handlers
  // ------------------------------------------------------------------
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = canvasRef.current!.getBoundingClientRect();
    const px = (e.clientX - rect.left) * (size / rect.width);
    const py = (e.clientY - rect.top)  * (size / rect.height);

    const hit = hitTest(px, py);
    if (hit !== null) {
      const ax   = AXIS_DEFS[hit];
      const dist = Math.max(new THREE.Vector3(camPos.x, camPos.y, camPos.z).length(), 1);
      onSnap((ax.dir as unknown as THREE.Vector3).clone().multiplyScalar(dist));
      return;
    }

    draggingRef.current    = true;
    lastMouseRef.current   = { x: e.clientX, y: e.clientY };
  }, [camPos, hitTest, onSnap, size]);

  const handleMouseMoveCb = useCallback((e: MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (!draggingRef.current) {
      // Update hover highlight
      const rect = canvas.getBoundingClientRect();
      const px = (e.clientX - rect.left) * (size / rect.width);
      const py = (e.clientY - rect.top)  * (size / rect.height);
      const inside = px >= 0 && py >= 0 && px <= size && py <= size;
      const prev = hoveredRef.current;
      hoveredRef.current = inside ? hitTest(px, py) : null;
      if (hoveredRef.current !== prev) draw();
      return;
    }

    const dx = e.clientX - lastMouseRef.current.x;
    const dy = e.clientY - lastMouseRef.current.y;
    lastMouseRef.current = { x: e.clientX, y: e.clientY };
    onOrbitDelta(dx, dy);
  }, [hitTest, draw, size, onOrbitDelta]);

  const handleMouseUpCb = useCallback(() => { draggingRef.current = false; }, []);

  const handleMouseLeave = useCallback(() => {
    if (hoveredRef.current !== null) { hoveredRef.current = null; draw(); }
  }, [draw]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMoveCb);
    window.addEventListener('mouseup',   handleMouseUpCb);
    return () => {
      window.removeEventListener('mousemove', handleMouseMoveCb);
      window.removeEventListener('mouseup',   handleMouseUpCb);
    };
  }, [handleMouseMoveCb, handleMouseUpCb]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{
        position:     'absolute',
        bottom:       12,
        left:         12,
        width:        size,
        height:       size,
        cursor:       'grab',
        borderRadius: '50%',
        background:   'rgba(0,0,0,0.28)',
        userSelect:   'none',
        zIndex:       200,
        pointerEvents: 'auto',
      }}
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseLeave}
    />
  );
};

export default AxisGizmo;
